type PermissionNode = {
  url?: unknown;
  children?: unknown;
};

type PermissionPayload = {
  permissions2?: unknown;
  data?: {
    permissions2?: unknown;
  };
  user?: {
    permissions2?: unknown;
  };
};

type PermissionSource = readonly string[] | null | undefined;

const PERMISSION_ALIASES: Record<string, string[]> = {
  resource: ["resources"],
  resources: ["resource"],
};

function getPermissionVariants(url: string): string[] {
  const normalized = url.trim();
  if (!normalized) return [];

  const variants = new Set<string>([
    normalized,
    normalized.replace(/_/g, "-"),
    normalized.replace(/-/g, "_"),
  ]);

  for (const variant of [...variants]) {
    const aliasTargets = PERMISSION_ALIASES[variant];
    if (!aliasTargets) continue;
    aliasTargets.forEach((alias) => variants.add(alias));
  }

  return [...variants];
}

function addPermissionVariants(url: string, bucket: Set<string>) {
  getPermissionVariants(url).forEach((variant) => bucket.add(variant));
}

function flattenPermissionNodes(nodes: unknown, bucket: Set<string>) {
  if (!Array.isArray(nodes)) return;

  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;

    const permissionNode = node as PermissionNode;

    if (typeof permissionNode.url === "string") {
      addPermissionVariants(permissionNode.url, bucket);
    }

    flattenPermissionNodes(permissionNode.children, bucket);
  }
}

function getPermissions2Source(payload?: PermissionPayload): unknown {
  return payload?.permissions2 ?? payload?.data?.permissions2 ?? payload?.user?.permissions2;
}

function toPermissionSet(permissions: PermissionSource): Set<string> {
  if (!Array.isArray(permissions)) return new Set<string>();
  return new Set(permissions);
}

function hasPermissionKey(permissionSet: Set<string>, key: string): boolean {
  const variants = getPermissionVariants(key);
  return variants.some((variant) => permissionSet.has(variant));
}

function hasAnyChildPermission(permissionSet: Set<string>, parent: string): boolean {
  const parentVariants = getPermissionVariants(parent);
  return [...permissionSet].some((permission) =>
    parentVariants.some((variant) => permission.startsWith(`${variant}.`))
  );
}

export function hasPermission(
  permissions: PermissionSource,
  parent: string,
  child?: string,
): boolean {
  const permissionSet = toPermissionSet(permissions);

  if (child && hasPermissionKey(permissionSet, child)) {
    return true;
  }

  if (!child) {
    return (
      hasPermissionKey(permissionSet, parent) ||
      hasAnyChildPermission(permissionSet, parent)
    );
  }

  if (!hasPermissionKey(permissionSet, parent)) {
    return false;
  }

  // Some roles only receive a parent permission (e.g. "logs") without explicit
  // child keys; in that case treat parent as full access for that module.
  return !hasAnyChildPermission(permissionSet, parent);
}

export function normalizePermissions(payload?: PermissionPayload): string[] {
  const permissions = new Set<string>();

  const permissions2 = getPermissions2Source(payload);
  if (permissions2) {
    flattenPermissionNodes(permissions2, permissions);
  }

  return [...permissions];
}